import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const CookiePreferenceCenter = ({ isOpen, onClose, onSave }) => {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  const cookieCategories = [
    {
      id: 'necessary',
      title: 'Strictly Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They cannot be disabled.',
      required: true,
      examples: ['Session management', 'Security tokens', 'Language preferences'],
      icon: 'Shield'
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization features.',
      required: false,
      examples: ['User preferences', 'Chat widgets', 'Video players'],
      icon: 'Settings'
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website.',
      required: false,
      examples: ['Google Analytics', 'Page views', 'User behavior tracking'],
      icon: 'BarChart3'
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      description: 'These cookies are used to deliver personalized advertisements and track campaign performance.',
      required: false,
      examples: ['Ad targeting', 'Social media pixels', 'Conversion tracking'],
      icon: 'Target'
    }
  ];

  const handlePreferenceChange = (categoryId, value) => {
    if (categoryId === 'necessary') return; // Cannot disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [categoryId]: value
    }));
  };

  const handleAcceptAll = () => {
    const allAccepted = cookieCategories?.reduce((acc, category) => {
      acc[category.id] = true;
      return acc;
    }, {});
    setPreferences(allAccepted);
    onSave(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = cookieCategories?.reduce((acc, category) => {
      acc[category.id] = category?.required;
      return acc;
    }, {});
    setPreferences(onlyNecessary);
    onSave(onlyNecessary);
  };

  const handleSavePreferences = () => {
    onSave(preferences);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-brand shadow-brand-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-brand-sm">
                <Icon name="Cookie" size={24} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Cookie Preferences</h2>
                <p className="text-sm text-muted-foreground">Manage your cookie and privacy settings</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-muted/50 rounded-brand p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div className="text-sm">
                <p className="text-foreground font-medium mb-1">About Cookies</p>
                <p className="text-muted-foreground">
                  We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                  You can customize your preferences below.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {cookieCategories?.map((category) => (
              <div key={category?.id} className="border border-border rounded-brand p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="p-2 bg-muted rounded-brand-sm">
                      <Icon name={category?.icon} size={18} className="text-foreground" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium text-foreground">{category?.title}</h3>
                        {category?.required && (
                          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-brand-xs font-medium">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{category?.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {category?.examples?.map((example, index) => (
                          <span key={index} className="px-2 py-1 bg-muted text-xs rounded-brand-xs text-muted-foreground">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    <Checkbox
                      checked={preferences?.[category?.id]}
                      onChange={(e) => handlePreferenceChange(category?.id, e?.target?.checked)}
                      disabled={category?.required}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-brand p-4">
            <h4 className="font-medium text-foreground mb-2">Data Processing Information</h4>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Data is processed in accordance with GDPR and local privacy laws</p>
              <p>• You can withdraw consent at any time through this preference center</p>
              <p>• Some cookies may be set by third-party services we use</p>
              <p>• Cookie data is retained for a maximum of 24 months</p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="flex-1"
            >
              Reject All
            </Button>
            <Button
              variant="secondary"
              onClick={handleSavePreferences}
              className="flex-1"
            >
              Save Preferences
            </Button>
            <Button
              variant="default"
              onClick={handleAcceptAll}
              className="flex-1 bg-gradient-to-r from-primary to-accent"
            >
              Accept All
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center mt-3">
            By continuing to use our website, you agree to our use of cookies as described in our Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferenceCenter;